import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { of, throwError } from 'rxjs';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { UnitOfMeasure, UnitOfMeasureConversion, UnitOfMeasureSystem } from '../../models/UnitOfMeasure';

@Injectable({
  providedIn: 'root'
})
export class UnitOfMeasureService {
  public BASE_URL =''
  
  constructor(
    private httpClient: HttpClient
  ) {
    
  }

  public getHeaders() {
    let header = new HttpHeaders();
    return header;
  }

  public getBaseUrl(){ 
    return `${window['environmentVariableConfig']['configUIApiUrl']}`;
  }


  public getUnitSystemByUser() {
    this.BASE_URL = this.getBaseUrl();
    return this.httpClient.get(`${this.BASE_URL}/api/oh-module/configuration/metadata/unitsystemandtimezone/byuser`)
    .pipe(
      catchError(() => of(null))
    )
  }

  public getUnitSystems() {
    this.BASE_URL = this.getBaseUrl();
    return this.httpClient.get(`${this.BASE_URL}/api/unit-of-measurement/metadata/measurementUnit/systems`)
    .pipe(
      catchError(() => of(null))
    )
  }
  
  public getUnitSystemDetails(unitSystemId: string) {
    this.BASE_URL = this.getBaseUrl();
    return this.httpClient.get(`${this.BASE_URL}/api/unit-of-measurement/measurementUnits/byUnitSystemId/${unitSystemId}`)
    .pipe(
      catchError(() => of(null))
    )
  }

  public getUoMByUnitSystemAndType(unitSystemId: string, unitType: string) {
    this.BASE_URL = this.getBaseUrl();
    let params = new HttpParams();

    if(unitSystemId) {
      params = params.set('unitSystemId', unitSystemId)
    }

    if(unitType) {
      params = params.set('unitType', unitType)
    }

    return this.httpClient.get(`${this.BASE_URL}/api/unit-of-measurement/metadata/measurementUnit/byunitsystemandtype`, {params: params})
    .pipe(
      map(data => data['UoMs']),
      catchError(() => of(null))
    )
  }


  public getUoMConversions() {
    this.BASE_URL = this.getBaseUrl();
    return this.httpClient.get(`${this.BASE_URL}/api/unit-of-measurement/metadata/measurementUnit/conversions`)
    .pipe(
      map(data => data['UoMs']),
      catchError(() => [])
    )
  }

  public getUoM(queryString: string) {
    this.BASE_URL = this.getBaseUrl();
    let params = new HttpParams();
    params.set('QueryString', queryString);


    return this.httpClient.get(`${this.BASE_URL}/api/unit-of-measurement/metadata/measurementUnit`)
    .pipe(
      map( data => data),
      catchError(() => of({}))
    )
  }


  public getUserDefaultUoM() {
    return this.getUnitSystemByUser()
    .pipe(
      map(data => {
        if(data?.['Unit System']) {
          return {
            unitSystemId: data['Unit System'],
            userTimeZone: data['TimeZone']
          }
        }
        throwError(null)
      }),
      mergeMap(({unitSystemId,userTimeZone}) => 
        this.getUnitSystemDetails(unitSystemId).pipe(
          map((data) =>{
            if(data?.['UoMs']) {
              return {
                userDefaultSystemId: unitSystemId,
                userDefaultUoMs: data['UoMs'],
                userTimeZone: userTimeZone
              }
            }
            throwError(null)
          })
        )
      )
    )
  }

  public getStandardUoMSystem(){
    return this.getUnitSystems()
    .pipe(
      map(data => {
        if(data?.['UoMSystems']) {
          const standardSystem: UnitOfMeasureSystem = data['UoMSystems'].find(uom => uom.IsStandardUnitSystem)
          if(standardSystem) {
            return standardSystem.Id
          }
          throwError(null)
        }
      }),
      mergeMap((unitSystemId) => 
        this.getUnitSystemDetails(unitSystemId).pipe(
          map((data) => {
            if(data?.['UoMs']) {
              return {
                standardUnitSystemId: unitSystemId,
                standardUoMs: data[`UoMs`]
              }
            }
            throwError(null)
          })
        )
      )
    )
  }
  
  public getUoMPerType(measurementType: string, userDefaultUoMs : UnitOfMeasure[]) {
    let uomPerType = userDefaultUoMs.filter(uom => uom.MeasurementType.toLowerCase() === measurementType.toLowerCase())
    let uom;
    if(uomPerType.length > 1) {
      uom = uomPerType.find(uom => uom.IsPrimary)
      if(!uom ) {
        uom = uomPerType[0]
      }
    } else {
      uom = uomPerType[0]
    }
    return uom;
  }

  public getUoMConversionFormula(fromUnitId, toUnitId, uomConversions) {
    if(fromUnitId === toUnitId) {
      return null;
    }
    const conversionFormula = uomConversions.find(conversion => conversion.fromUnitId === fromUnitId && conversion.toUnitId === toUnitId)
    return conversionFormula;
  }

  public calculateUoM(fromUoM: number, conversionFormula: UnitOfMeasureConversion) {
    if(conversionFormula === null) {
      return fromUoM
    }
    let {a, b, c, d} = conversionFormula;
    let resultUom = (a + fromUoM*b) / (c*fromUoM + d)
    return resultUom;
  }
}
